import { Injectable } from '@jkhy/frontend-utils/di';
import { ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation/navigation.service';
import { searchParamsToObject } from '@treasury/utils/functions/url.helpers';
import { WindowService } from '@treasury/utils/services/window.service';
import { Router } from '@vaadin/router';
import { routes } from '../routes';

@Injectable()
export class BoNavigationService extends NavigationService {
    constructor(
        window: WindowService,
        private config: ConfigurationService
    ) {
        super(window);
        this.setRouteBase(config.basePath);
    }

    private readonly router = new Router();

    public async setOutlet(outlet: Node | (() => Promise<Node>)) {
        await super.setOutlet(outlet);

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.router.setOutlet(this.outlet!);
        this.router.setRoutes(routes);
    }

    public getRoute(route: string) {
        // if banno auth is enabled, and the route does not have a guid, add the guid to the route
        if (this.config.bannoAuthEnabled) {
            const activeRoute = window.location.pathname;

            const guidPattern = /[0-9a-fA-F-]{36}/;
            const guid = activeRoute.match(guidPattern);
            const routeHasGuid = route.match(guidPattern);
            if (guid && !routeHasGuid) {
                if (route.startsWith(this.config.basePath)) {
                    route = route.replace(
                        this.config.basePath,
                        `${this.config.basePath}${guid[0]}/`
                    );
                } else {
                    route = `${this.config.basePath}${guid[0]}/${route}`;
                }
            }
        }

        // get rid of all extra slashes
        route = route.replace(/([^:]\/)\/+/g, '$1');

        return route;
    }

    protected async performNavigation(route: string) {
        route = this.getRoute(route);
        const success = Router.go(route);

        if (!success) {
            throw new Error(
                'Could not navigate. There was no Vaadin router available to handle the route transition. Did you set the correct base route?'
            );
        }

        await this.router.ready;
    }

    protected async getActiveRoute() {
        await this.router.ready;
        return this.router.location.pathname;
    }

    protected async getUrlParams() {
        await this.router.ready;
        const routeParams = this.router.location.params;
        const queryParams = searchParamsToObject(this.window.searchParams) ?? {};
        const combined = {
            ...routeParams,
            ...queryParams,
        };
        return Promise.resolve(combined);
    }
}
